import React, { useEffect, useRef, useState } from "react";
import { Button, TextField, CircularProgress, Typography } from "@mui/material";
import { twMerge } from "tailwind-merge";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { useCreateBulkShipmentsMutation } from "store/services/shipmentApi";
import {
  useLazyGetDDLBranchListQuery,
  useLazyGetDDLDestinationBranchListQuery,
} from "store/services/branchApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";

import { useAuth } from "hooks";

const getRowWarnings = (row) => {
  const warnings = [];
  if (!row?.vendor?._id) warnings.push("Vendor not resolved");
  if (!row?.receiver?.name) warnings.push("Receiver name missing");
  if (!row?.receiver?.phone) warnings.push("Receiver phone missing");
  if (!row?.sourceBranch?._id) warnings.push("Source branch not resolved");
  if (!row?.destinationBranch?._id) warnings.push("Destination branch not resolved");
  // if (row?.transitBranch !== null && !row?.transitBranch?._id)
  //   warnings.push("Transit branch not resolved");
  return warnings;
};

const ShipmentReviewScreen = () => {
  const location = useLocation();
  const { shipments = [], vendor } = location.state || {};
  const navigate = useNavigate();
  const { isVendor, isVendorStaff } = useAuth();
  const { control, reset, handleSubmit } = useForm({
    defaultValues: { reviewData: [] },
  });

  const { viewEntity, loggedInUser } = useAuth();

  const account =
    viewEntity?.entity?.account?._id || loggedInUser?.account?._id || null;
  const entity = viewEntity?.entity?._id || loggedInUser?.entity?._id || null;

  const watchReviewData = useWatch({ control, name: "reviewData" });

  const [createBulkShipments, { isLoading: creatingShipments }] =
    useCreateBulkShipmentsMutation();

  const [fetchSourceBranches, { data: sourceBranchData, isLoading: loadingSourceBranch }] =
    useLazyGetDDLBranchListQuery();

  const [fetchDestinationBranches, { data: destinationBranchData, isLoading: loadingDestinationBranch }] =
    useLazyGetDDLDestinationBranchListQuery();

  const [
    fetchVendors,
    { isLoading: loadingVendors, isFetching: fetchingVendors },
  ] = useLazyGetDdlVendorProfileQuery();

  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const sourceBranchList = React.useMemo(
    () =>
      Array.isArray(sourceBranchData?.data)
        ? sourceBranchData.data
        : Array.isArray(sourceBranchData)
          ? sourceBranchData
          : [],
    [sourceBranchData]
  );

  const destinationBranchList = React.useMemo(
    () =>
      Array.isArray(destinationBranchData?.data)
        ? destinationBranchData.data
        : Array.isArray(destinationBranchData)
          ? destinationBranchData
          : [],
    [destinationBranchData]
  );

  const isBusy =
    loadingSourceBranch ||
    loadingDestinationBranch ||
    loadingVendors ||
    fetchingVendors;

  React.useEffect(() => {
    if (account && entity) {
      fetchVendors({ account, entity });
      fetchSourceBranches({ account, entity });
      fetchDestinationBranches({ account, entity });
    }
  }, [account, entity, fetchVendors, fetchSourceBranches, fetchDestinationBranches]);

  const addItem = (rowIndex) => {
    const currentRow = watchReviewData[rowIndex];
    const newItem = { itemReference: "", name: "", weight: null };
    const updatedData = [...watchReviewData];
    updatedData[rowIndex] = {
      ...currentRow,
      shipmentDetails: [...currentRow.shipmentDetails, newItem],
    };
    reset({ reviewData: updatedData });
  };

  const removeItem = (rowIndex, itemIndex) => {
    const currentRow = watchReviewData[rowIndex];
    const updatedDetails = currentRow.shipmentDetails.filter(
      (_, index) => index !== itemIndex
    );
    const updatedData = [...watchReviewData];
    updatedData[rowIndex] = {
      ...currentRow,
      shipmentDetails: updatedDetails.length
        ? updatedDetails
        : [{ itemReference: "", name: "", weight: null }],
    };
    reset({ reviewData: updatedData });
  };

  const updateFieldInShipment = (rowIndex, field, value) => {
    const updatedData = [...watchReviewData];
    updatedData[rowIndex] = { ...updatedData[rowIndex], [field]: value };
    reset({ reviewData: updatedData });
  };

  const groupShipments = (shipments) => {
    const grouped = {};
    shipments.forEach((shipment) => {
      const key = `${shipment["SN"]}`;
      if (!grouped[key]) {
        grouped[key] = { ...shipment, shipmentDetails: [] };
      }
      grouped[key].shipmentDetails.push({
        name: shipment["Item Name"] || "",
        itemReference: shipment["Item Reference"] || "",
        weight: Number(shipment["Weight"]) || null,
      });
    });
    return Object.values(grouped);
  };

  const normalizePhone = (phone) => String(phone || "").replace(/[-+\s]/g, "");

  const formattedShipments = React.useMemo(() => {
    if (isBusy || !shipments || shipments.length === 0) return [];

    const groupedShipments = groupShipments(shipments);
    return groupedShipments.map((shipment) => {
      const receiverName = String(shipment["Receiver"] || "").trim();
      const receiverPhone = normalizePhone(shipment["Receiver Phone"]);

      const sourceBranch = sourceBranchList.find(
        (item) => item?.code === shipment["Source Branch"]
      );
      const destinationBranch = destinationBranchList.find(
        (item) => item?.code === shipment["Destination Branch"]
      );

      // const transitBranchCode = shipment["Transit Branch"];
      // const transitBranch = transitBranchCode
      //   ? destinationBranchList.find((item) => item?.code === transitBranchCode)
      //   : null;

      const selectedVendor = vendor && vendor._id ? vendor : null;

      return {
        sn: shipment["SN"],
        vendor: {
          _id: selectedVendor?._id || "",
          name: selectedVendor?.vendor?.name || shipment["Sender"] || "",
          error: !selectedVendor,
        },
        receiver: {
          name: receiverName,
          phone: receiverPhone,
        },
        sourceBranch: {
          _id: sourceBranch?._id ?? "",
          name: sourceBranch?.name ?? shipment["Source Branch"] ?? "",
          error: !sourceBranch,
        },
        destinationBranch: {
          _id: destinationBranch?._id ?? "",
          name: destinationBranch?.name ?? shipment["Destination Branch"] ?? "",
          error: !destinationBranch,
        },
        // transitBranch: transitBranchCode
        //   ? {
        //     _id: transitBranch?._id ?? "",
        //     name: transitBranch?.name ?? transitBranchCode,
        //     error: !transitBranch,
        //   }
        //   : null,
        transitBranch: null,
        reference: shipment["Reference"] || "",
        productDescription: String(shipment["Description"] || "").trim(),
        shipmentDetails: shipment.shipmentDetails,
        pickupLocation: {
          lat: Number(shipment["Pickup Location"]) || null,
          lon: null,
        },
        deliverLocation: {
          lat: Number(shipment["Deliver Location"]) || null,
          lon: null,
        },
        weight: Number(shipment["Weight"]) || 0,
        dimensions: {
          length: Number(shipment["Dimensions"]?.split("x")[0]) || null,
          width: Number(shipment["Dimensions"]?.split("x")[1]) || null,
          height: Number(shipment["Dimensions"]?.split("x")[2]) || null,
        },
        shipmentPickupTime:
          shipment["Shipment Pickup Time"] || new Date().toISOString(),
        shipmentDeliveryTime: shipment["Shipment Delivery Time"] || "",
        shipmentAmount: Number(shipment["Shipment Amount"]) || 0,
        COD: Number(shipment["COD"]) || 0,
      };
    });
  }, [shipments, sourceBranchList, destinationBranchList, isBusy, vendor]);

  const prevFormattedShipmentsRef = useRef([]);

  useEffect(() => {
    const currentFormatted = JSON.stringify(formattedShipments);
    const prevFormatted = JSON.stringify(prevFormattedShipmentsRef.current);
    if (formattedShipments.length > 0 && currentFormatted !== prevFormatted) {
      reset({ reviewData: formattedShipments });
      prevFormattedShipmentsRef.current = formattedShipments;
      setTotalCount(formattedShipments.length);
    }
  }, [formattedShipments, reset]);

  const handleShipmentCreate = async (data) => {
    try {
      if (!account || !entity) {
        toast.error("Account and Entity are required for shipment creation.");
        return;
      }

      const invalidShipments = data.reviewData.filter(
        (item) =>
          !item.sourceBranch?._id ||
          !item.destinationBranch?._id ||
          !item.receiver?.name ||
          !item.receiver?.phone ||
          !item.vendor?._id
      );
      if (invalidShipments.length > 0) {
        toast.error(
          "All shipments must have valid Source Branch, Destination Branch, Receiver Name, Receiver Phone, and Vendor."
        );
        return;
      }

      setProcessedCount(0);
      const payload = data.reviewData.map((item) => ({
        ...item,
        account,
        entity,
        vendor: item.vendor?._id || null,
        receiver: {
          name: item.receiver?.name,
          phone: normalizePhone(item.receiver?.phone),
        },
        sourceBranch: item.sourceBranch?._id,
        destinationBranch: item.destinationBranch?._id,
        // transitBranch: item.transitBranch?._id || null,
        reference: item.reference || "",
        productDescription: item.productDescription || undefined,
        pickupLocation: item.pickupLocation || {},
        deliverLocation: item.deliverLocation || {},
        dimensions: item.dimensions || {},
        shipmentDetails: item.shipmentDetails || [
          { itemReference: "", name: "", weight: null },
        ],
        COD: Number(item.COD) || 0,
      }));

      const response = await createBulkShipments({ shipments: payload }).unwrap();
      setProcessedCount(response.importedCount);

      const listPath = isVendor
        ? "/app/vendor/shipment/list"
        : isVendorStaff
          ? "/app/staff/shipment/list"
          : "/app/crm/shipment/list";

      navigate("/app/crm/shipment/import-summary", {
        state: {
          results: response.results,
          leads: data.reviewData,
          importedCount: response.importedCount,
          totalCount: payload.length,
          listPath,
        },
      });
    } catch (err) {
      toast.error(
        err?.data?.message || err?.error || "Couldn't create shipments!"
      );
      setProcessedCount(0);
    }
  };

  const handleClose = () => {
    navigate("/app/crm/shipment/list");
  };

  if (isBusy) {
    return (
      <div className="py-4 text-center text-gray-500 dark:text-gray-400">
        Loading dependencies...
      </div>
    );
  }

  if (!sourceBranchList.length && !destinationBranchList.length) {
    return (
      <div className="py-4 text-center text-red-500 dark:text-red-400">
        No branches available. Please ensure branches are set up or check query parameters.
      </div>
    );
  }

  if (!watchReviewData || watchReviewData.length === 0) {
    return (
      <div className="py-4 text-center text-gray-600 dark:text-gray-400">
        No shipment data to review. Please ensure the Excel file has valid column names and data.
      </div>
    );
  }

  const uniqueRows = watchReviewData.filter(
    (row, idx, arr) => arr.findIndex((r) => r.sn === row.sn) === idx
  );
  const willSkipCount = uniqueRows.filter((r) => getRowWarnings(r).length > 0).length;
  const willImportCount = uniqueRows.length - willSkipCount;

  const headers = [
    "Status",
    "Receiver Name",
    "Receiver Phone",
    "Source Branch",
    "Destination Branch",
    "Reference",
    "Description",
    "COD",
    "Item",
    "Item Name",
    "Item Reference",
    "Weight",
    // "Transit Branch",
    "Actions",
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
        <h2 className="mb-4 text-2xl font-bold text-gray-900 dark:text-gray-100">
          Review Shipments for Vendor:{" "}
          {vendor?.vendor?.name || vendor?.name || "Unknown Vendor"}
        </h2>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 dark:border-green-800 dark:bg-green-900/30">
            <CheckCircleOutlineIcon className="text-green-600 dark:text-green-400" style={{ fontSize: 16 }} />
            <span className="text-sm font-semibold text-green-700 dark:text-green-300">
              {willImportCount} will be imported
            </span>
          </div>

          {willSkipCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 dark:border-red-800 dark:bg-red-900/30">
              <WarningAmberIcon className="text-red-500 dark:text-red-400" style={{ fontSize: 16 }} />
              <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                {willSkipCount} will be skipped — fix errors below
              </span>
            </div>
          )}
        </div>

        {creatingShipments && (
          <div className="mb-4 flex items-center justify-center space-x-4">
            <CircularProgress size={24} />
            <Typography variant="body1" color="textSecondary">
              Processing {processedCount} out of {totalCount} shipments...
            </Typography>
          </div>
        )}

        <div className="relative max-h-[600px] overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 shadow-sm dark:border-gray-700">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-700">
              <tr>
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="whitespace-nowrap border-b border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:border-gray-600 dark:text-gray-200"
                    style={
                      header === "Status"
                        ? { minWidth: "160px" }
                        : header === "Item Name" || header === "Item Reference"
                          ? { minWidth: "200px" }
                          : header === "Item"
                            ? { minWidth: "1px" }
                            : header === "Weight" || header === "COD"
                              ? { minWidth: "120px" }
                              : header === "Actions"
                                ? { minWidth: "100px" }
                                : header === "Reference"
                                  ? { minWidth: "150px" }
                                  : header === "Description"
                                    ? { minWidth: "200px" }
                                    : { minWidth: "250px" }
                    }
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {watchReviewData.map((row, rowIndex) => {
                const rowWarnings = getRowWarnings(row);
                const hasWarning = rowWarnings.length > 0;

                return row.shipmentDetails.map((item, itemIndex) => (
                  <tr
                    key={`${rowIndex}-${itemIndex}`}
                    className={twMerge(
                      "transition-colors duration-200",
                      hasWarning
                        ? "bg-red-50 dark:bg-red-900/20"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    )}
                    style={
                      hasWarning
                        ? { borderLeft: "3px solid #ef4444" }
                        : { borderLeft: "3px solid transparent" }
                    }
                  >
                    {itemIndex === 0 ? (
                      <td
                        className="border-b border-gray-200 px-3 py-2 dark:border-gray-700"
                        rowSpan={row.shipmentDetails.length}
                        style={{ verticalAlign: "top", paddingTop: 10 }}
                      >
                        {hasWarning ? (
                          <div className="flex flex-col gap-1">
                            <span className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-800/40 dark:text-red-300">
                              <WarningAmberIcon style={{ fontSize: 12 }} />
                              Will skip
                            </span>
                            <ul className="mt-1 space-y-0.5">
                              {rowWarnings.map((w, i) => (
                                <li key={i} className="text-xs text-red-500 dark:text-red-400">
                                  · {w}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-800/40 dark:text-green-300">
                            <CheckCircleOutlineIcon style={{ fontSize: 12 }} />
                            Ready
                          </span>
                        )}
                      </td>
                    ) : null}

                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].receiver.name`}
                        control={control}
                        defaultValue={row?.receiver?.name || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Receiver Name"
                            fullWidth
                            variant="outlined"
                            size="small"
                            disabled
                            className="text-sm"
                            onChange={(e) => {
                              field.onChange(e);
                              updateFieldInShipment(rowIndex, "receiver", {
                                name: String(e.target.value || ""),
                                phone: String(row?.receiver?.phone || ""),
                              });
                            }}
                          />
                        )}
                      />
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].receiver.phone`}
                        control={control}
                        defaultValue={row?.receiver?.phone || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Receiver Phone"
                            fullWidth
                            variant="outlined"
                            size="small"
                            disabled
                            className="text-sm"
                            onChange={(e) => {
                              field.onChange(e);
                              updateFieldInShipment(rowIndex, "receiver", {
                                name: String(row?.receiver?.name || ""),
                                phone: String(e.target.value || ""),
                              });
                            }}
                          />
                        )}
                      />
                    </td>

                    {/* Source Branch — restricted list */}
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].sourceBranch`}
                        control={control}
                        defaultValue={row.sourceBranch}
                        render={({ field }) => (
                          <select
                            {...field}
                            className={twMerge(
                              "w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400",
                              row?.sourceBranch?.error ? "border-red-400 dark:border-red-400" : ""
                            )}
                            value={field.value?._id || ""}
                            onChange={(e) => {
                              const selectedBranch = sourceBranchList.find(
                                (branch) => branch._id === e.target.value
                              );
                              field.onChange(selectedBranch);
                              updateFieldInShipment(rowIndex, "sourceBranch", selectedBranch);
                            }}
                          >
                            <option value="" disabled>
                              {row?.sourceBranch?.name || "Select a source branch"}
                            </option>
                            {sourceBranchList.map((branch) => (
                              <option key={branch._id} value={branch._id}>
                                {branch.name} - {branch.district?.name}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    </td>

                    {/* Destination Branch — all branches */}
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].destinationBranch`}
                        control={control}
                        defaultValue={row.destinationBranch}
                        render={({ field }) => (
                          <select
                            {...field}
                            className={twMerge(
                              "w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400",
                              row?.destinationBranch?.error ? "border-red-400 dark:border-red-400" : ""
                            )}
                            value={field.value?._id || ""}
                            onChange={(e) => {
                              const selectedBranch = destinationBranchList.find(
                                (branch) => branch._id === e.target.value
                              );
                              field.onChange(selectedBranch);
                              updateFieldInShipment(rowIndex, "destinationBranch", selectedBranch);
                            }}
                          >
                            <option value="" disabled>
                              {row?.destinationBranch?.name || "Select a destination branch"}
                            </option>
                            {destinationBranchList.map((branch) => (
                              <option key={branch._id} value={branch._id}>
                                {branch.name} - {branch.district?.name}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    </td>

                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].reference`}
                        control={control}
                        defaultValue={row.reference || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Reference"
                            fullWidth
                            variant="outlined"
                            size="small"
                            onChange={(e) => {
                              field.onChange(e);
                              updateFieldInShipment(rowIndex, "reference", e.target.value);
                            }}
                          />
                        )}
                      />
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].productDescription`}
                        control={control}
                        defaultValue={row.productDescription || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Description"
                            fullWidth
                            variant="outlined"
                            size="small"
                            onChange={(e) => {
                              field.onChange(e);
                              updateFieldInShipment(rowIndex, "productDescription", e.target.value);
                            }}
                          />
                        )}
                      />
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].COD`}
                        control={control}
                        defaultValue={row.COD}
                        rules={{ required: "COD is required" }}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label="COD"
                            type="number"
                            fullWidth
                            variant="outlined"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 text-center dark:border-gray-700">
                      <span className="text-gray-900 dark:text-gray-100">{itemIndex + 1}</span>
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].shipmentDetails[${itemIndex}].name`}
                        control={control}
                        defaultValue={item.name || ""}
                        render={({ field }) => (
                          <TextField {...field} label="Item Name" fullWidth variant="outlined" size="small" />
                        )}
                      />
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].shipmentDetails[${itemIndex}].itemReference`}
                        control={control}
                        defaultValue={item.itemReference || ""}
                        render={({ field }) => (
                          <TextField {...field} label="Item Reference" fullWidth variant="outlined" size="small" />
                        )}
                      />
                    </td>
                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <Controller
                        name={`reviewData[${rowIndex}].shipmentDetails[${itemIndex}].weight`}
                        control={control}
                        defaultValue={item.weight || null}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Weight"
                            type="number"
                            fullWidth
                            variant="outlined"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value) || null)}
                          />
                        )}
                      />
                    </td>

                    {/* Transit Branch — all branches */}
                    {/* <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      {row.transitBranch !== null ? (
                        <Controller
                          name={`reviewData[${rowIndex}].transitBranch`}
                          control={control}
                          defaultValue={row.transitBranch}
                          render={({ field }) => (
                            <select
                              {...field}
                              className={twMerge(
                                "w-full rounded-md border border-gray-300 bg-white p-2 text-sm text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:ring-blue-400",
                                row?.transitBranch?.error ? "border-red-400 dark:border-red-400" : ""
                              )}
                              value={field.value?._id || ""}
                              onChange={(e) => {
                                const selectedBranch = destinationBranchList.find(
                                  (branch) => branch._id === e.target.value
                                );
                                const transitBranchValue = selectedBranch
                                  ? { _id: selectedBranch._id, name: selectedBranch.name, error: false }
                                  : row.transitBranch;
                                field.onChange(transitBranchValue);
                                updateFieldInShipment(rowIndex, "transitBranch", transitBranchValue);
                              }}
                            >
                              <option value="" disabled>
                                {row?.transitBranch?.name || "Select a transit branch"}
                              </option>
                              {destinationBranchList.map((branch) => (
                                <option key={branch._id} value={branch._id}>
                                  {branch.name} - {branch.district?.name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">N/A</span>
                      )}
                    </td> */}

                    <td className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
                      <div className="flex space-x-2">
                        {itemIndex === row.shipmentDetails.length - 1 && (
                          <Button variant="outlined" size="small" onClick={() => addItem(rowIndex)}>
                            <AddIcon fontSize="small" />
                          </Button>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => removeItem(rowIndex, itemIndex)}
                        >
                          <DeleteIcon fontSize="small" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            <p className="mb-2 font-semibold">Legend:</p>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <span className="font-medium">Green "Ready"</span>: Shipment will be imported.
              </li>
              <li>
                <span className="font-medium">Red "Will skip"</span>: Missing or unresolved fields — fix to include.
              </li>
              <li>
                <span className="inline-block rounded bg-red-400 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-500/50 dark:text-red-300">
                  Red border
                </span>
                : Field not found in system.
              </li>
            </ul>
          </div>
          <div className="flex space-x-4">
            <Button disabled={creatingShipments} onClick={handleClose} variant="contained" className="primary">
              Go Back
            </Button>
            <Button
              disabled={creatingShipments}
              onClick={handleSubmit(handleShipmentCreate)}
              variant="contained"
              className="primary"
            >
              Finalize Bulk Shipments
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentReviewScreen;